"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ColorPickerPanel = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const nano_theme_1 = require("nano-theme");
const context_1 = require("@jsonjoy.com/ui/lib/4-card/Popup/context");
const ContextMenu_1 = require("@jsonjoy.com/ui/lib/4-card/ContextMenu");
const context_2 = require("@jsonjoy.com/ui/lib/styles/context");
const ColorSwatch_1 = require("@jsonjoy.com/ui/lib/2-inline-block/ColorSwatch");
const ColorPicker_1 = require("@jsonjoy.com/ui/lib/4-card/ColorPicker");
const InputColor_1 = require("@jsonjoy.com/ui/lib/2-inline-block/InputColor");
const HslColor_1 = require("@jsonjoy.com/ui/lib/styles/color/HslColor");
const BasicButton_1 = require("@jsonjoy.com/ui/lib/2-inline-block/BasicButton");
const Tabs_1 = require("@jsonjoy.com/ui/lib/3-list-item/Tabs");
const palette_1 = require("../util/palette");
const color_1 = require("../behavior/color");
const font_1 = require("../behavior/font");
const Separator_1 = require("@jsonjoy.com/ui/lib/3-list-item/Separator");
const MiniTitle_1 = require("@jsonjoy.com/ui/lib/3-list-item/MiniTitle");
const use_t_1 = require("use-t");
const PANEL_WIDTH = 410;
const containerClass = (0, nano_theme_1.rule)({
    d: 'flex',
    fld: 'column',
    gap: '14px',
    pd: '12px',
    w: PANEL_WIDTH + 'px',
});
const tabsCenterClass = (0, nano_theme_1.rule)({
    d: 'flex',
    jc: 'center',
    pd: '12px 0 0',
});
const tabsInlineClass = (0, nano_theme_1.rule)({
    d: 'inline-block',
});
const sectionClass = (0, nano_theme_1.rule)({
    d: 'flex',
    fld: 'column',
    gap: '12px',
});
const gridClass = (0, nano_theme_1.rule)({
    d: 'grid',
    gap: '6px',
});
const pickerSectionClass = (0, nano_theme_1.rule)({
    d: 'flex',
    ai: 'stretch',
    gap: '12px',
});
const previewBoxClass = (0, nano_theme_1.rule)({
    d: 'flex',
    fld: 'column',
    ai: 'center',
    jc: 'center',
    gap: '8px',
    bdrad: '8px',
    pd: '12px',
    flex: 1,
    minW: 0,
    minH: '128px',
    fz: '14px',
    fw: 500,
    textAlign: 'center',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
});
const actionRowClass = (0, nano_theme_1.rule)({
    d: 'flex',
    ai: 'center',
    gap: '8px',
});
const actionInputClass = (0, nano_theme_1.rule)({
    flex: 1,
    minW: 0,
});
const tabContentClass = (0, nano_theme_1.rule)({
    d: 'flex',
    fld: 'column',
    gap: '14px',
});
const ColorPickerPanel = ({ mutxt, kind }) => {
    const [t] = (0, use_t_1.useT)();
    const popup = (0, context_1.usePopup)();
    const styles = (0, context_2.useStyles)();
    const recent = mutxt.inline.recentColors.use();
    const editorFont = mutxt.font.use();
    const editorFontFamily = font_1.FONT_FAMILIES[editorFont];
    React.useEffect(() => {
        mutxt.inline.setPopupOpen(true);
        return () => mutxt.inline.setPopupOpen(false);
    }, [mutxt]);
    const activeFg = (0, color_1.getActiveFg)(mutxt.editor);
    const activeBg = (0, color_1.getActiveBg)(mutxt.editor);
    const activeForKind = kind === 'fg' ? activeFg : activeBg;
    const [tab, setTab] = React.useState('presets');
    const initial = activeForKind || (kind === 'fg' ? '#1A1A1A' : '#FFFFFF');
    const [pickerColor, setPickerColor] = React.useState(HslColor_1.HslColor.from(initial) ?? new HslColor_1.HslColor(0, 0, 0));
    const [pickerInput, setPickerInput] = React.useState((0, palette_1.normalizeHex)(HslColor_1.HslColor.from(initial)?.toRgb().hex() || initial));
    mutxt.version.use();
    const docColors = (0, color_1.collectDocumentColors)(mutxt.editor, kind, 10);
    const closePopup = () => popup?.close();
    const applyFg = (color) => {
        (0, color_1.setFg)(mutxt.editor, color);
        if (color)
            mutxt.inline.pushRecentColor(color);
        mutxt.sync(false);
    };
    const applyBg = (color) => {
        (0, color_1.setBg)(mutxt.editor, color);
        if (color)
            mutxt.inline.pushRecentColor(color);
        mutxt.sync(false);
    };
    const applyCombo = (combo) => {
        (0, color_1.setFg)(mutxt.editor, combo.fg);
        (0, color_1.setBg)(mutxt.editor, combo.bg);
        mutxt.inline.pushRecentColor(combo.fg);
        mutxt.inline.pushRecentColor(combo.bg);
        mutxt.sync(false);
    };
    const onTextSwatch = (c) => {
        applyFg(c);
        closePopup();
    };
    const onBgSwatch = (c) => {
        applyBg(c);
        closePopup();
    };
    const onComboSwatch = (combo) => {
        applyCombo(combo);
        closePopup();
    };
    const onPickerChange = (color) => {
        setPickerColor(color);
        setPickerInput((0, palette_1.normalizeHex)(color.toRgb().hex()));
    };
    const onPickerInputChange = (value) => {
        setPickerInput(value);
        if ((0, palette_1.isValidColor)(value)) {
            const c = HslColor_1.HslColor.from(value);
            if (c)
                setPickerColor(c);
        }
    };
    const onPickerCommit = () => {
        if (!(0, palette_1.isValidColor)(pickerInput))
            return;
        const hex = (0, palette_1.normalizeHex)(pickerInput);
        if (kind === 'fg')
            applyFg(hex);
        else
            applyBg(hex);
        closePopup();
    };
    const previewBg = kind === 'bg' ? pickerColor.toString() : styles.g(0, 0.04);
    const previewFg = kind === 'fg' ? pickerColor.toString() : styles.g(0.15);
    const preventMouseDown = (e) => {
        if (e.target.tagName !== 'INPUT')
            e.preventDefault();
    };
    const presetsContent = (React.createElement("div", { className: tabContentClass },
        kind === 'fg' ? (React.createElement("div", { className: sectionClass },
            React.createElement(MiniTitle_1.MiniTitle, null, t('Text')),
            React.createElement("div", { className: gridClass, style: { gridTemplateColumns: `repeat(${palette_1.QUICK_PALETTE_COLS}, 32px)`, justifyContent: 'space-between' } }, palette_1.TEXT_PALETTE.map((c) => (React.createElement(ColorSwatch_1.ColorSwatch, { key: 'text-' + c, kind: "fg", color: c, active: activeFg === c, tooltip: { renderTooltip: () => c }, onClick: () => onTextSwatch(c) })))))) : null,
        kind === 'bg' ? (React.createElement("div", { className: sectionClass },
            React.createElement(MiniTitle_1.MiniTitle, null, t('Background')),
            React.createElement("div", { className: gridClass, style: { gridTemplateColumns: `repeat(${palette_1.QUICK_PALETTE_COLS}, 32px)`, justifyContent: 'space-between' } }, palette_1.QUICK_PALETTE.map((c) => (React.createElement(ColorSwatch_1.ColorSwatch, { key: 'bg-' + c, kind: "bg", color: c, active: activeBg === c, tooltip: { renderTooltip: () => c }, onClick: () => onBgSwatch(c) })))))) : null,
        React.createElement("div", { className: sectionClass },
            React.createElement(MiniTitle_1.MiniTitle, null, t('Combination')),
            React.createElement("div", { className: gridClass, style: { gridTemplateColumns: `repeat(${palette_1.QUICK_PALETTE_COLS}, 32px)`, justifyContent: 'space-between' } }, palette_1.RECOMMEND_PALETTE.map((combo, idx) => (React.createElement(ColorSwatch_1.ColorSwatch, { key: 'rec-' + idx, kind: "fgbg", color: combo.bg, textColor: combo.fg, active: activeFg === combo.fg && activeBg === combo.bg, tooltip: { renderTooltip: () => `Text ${combo.fg} on ${combo.bg}` }, onClick: () => onComboSwatch(combo) }))))),
        recent.length > 0 || docColors.length > 0 ? (React.createElement("div", { className: sectionClass },
            recent.length > 0 ? (React.createElement(React.Fragment, null,
                React.createElement(MiniTitle_1.MiniTitle, null, t('Recent')),
                React.createElement("div", { className: gridClass, style: { gridTemplateColumns: `repeat(${palette_1.QUICK_PALETTE_COLS}, 32px)`, justifyContent: 'space-between' } }, recent.map((c) => (React.createElement(ColorSwatch_1.ColorSwatch, { key: 'recent-' + c, kind: kind === 'fg' ? 'fg' : 'bg', color: c, active: activeForKind === c, tooltip: { renderTooltip: () => c }, onClick: () => (kind === 'fg' ? onTextSwatch(c) : onBgSwatch(c)) })))))) : null,
            docColors.length > 0 ? (React.createElement(React.Fragment, null,
                React.createElement(MiniTitle_1.MiniTitle, null, t('In document')),
                React.createElement("div", { className: gridClass, style: { gridTemplateColumns: `repeat(${palette_1.QUICK_PALETTE_COLS}, 32px)`, justifyContent: 'space-between' } }, docColors.map((c) => (React.createElement(ColorSwatch_1.ColorSwatch, { key: 'doc-' + c, kind: kind === 'fg' ? 'fg' : 'bg', color: c, active: activeForKind === c, tooltip: { renderTooltip: () => c }, onClick: () => (kind === 'fg' ? onTextSwatch(c) : onBgSwatch(c)) })))))) : null)) : null));
    const customContent = (React.createElement("div", { className: tabContentClass },
        React.createElement(MiniTitle_1.MiniTitle, null, t('Pick a color')),
        React.createElement("div", { className: pickerSectionClass },
            React.createElement(ColorPicker_1.ColorPicker, { color: pickerColor, onChange: onPickerChange, noAlpha: true, style: { width: 200 } }),
            React.createElement("div", { className: previewBoxClass, style: {
                    background: previewBg,
                    color: previewFg,
                    border: `1px solid ${styles.g(0, 0.12)}`,
                    fontFamily: editorFontFamily,
                } },
                React.createElement("div", { style: { fontSize: '42px', width: '100%', lineHeight: 1 } }, "Ag"),
                React.createElement("div", { style: { fontSize: '11px', opacity: 0.85 } }, "Sphinx of black quartz"))),
        React.createElement("div", { className: actionRowClass },
            React.createElement("div", { className: actionInputClass },
                React.createElement(InputColor_1.InputColor, { value: pickerInput, onChange: onPickerInputChange, onEnter: (e) => {
                        e.preventDefault();
                        onPickerCommit();
                    }, placeholder: "#hex" })),
            React.createElement(BasicButton_1.BasicButton, { type: "button", width: 'auto', height: 32, rounder: true, fill: true, positive: true, disabled: !(0, palette_1.isValidColor)(pickerInput), onClick: onPickerCommit }, t('Apply')))));
    return (React.createElement(ContextMenu_1.ContextPane, null,
        React.createElement("div", { className: containerClass, onMouseDown: preventMouseDown },
            tab === 'presets' ? presetsContent : customContent,
            React.createElement("div", { style: { padding: '4px 0', margin: '0 -12px' } },
                React.createElement(Separator_1.Separator, null),
                React.createElement("div", { className: tabsCenterClass },
                    React.createElement("div", { className: tabsInlineClass },
                        React.createElement(Tabs_1.Tabs, { active: tab, onChange: (key) => setTab(key), items: [
                                { key: 'presets', label: t('Presets') },
                                { key: 'custom', label: t('Custom') },
                            ] })))))));
};
exports.ColorPickerPanel = ColorPickerPanel;
